import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListSubheader from '@mui/material/ListSubheader';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Paper from "@mui/material/Paper";
import {VariableSizeList} from 'react-window';
import {Typography} from '@mui/material';
import {isEmpty} from "../../../utils/GenericUtils";
import {getHelperStyle} from "../../../styles/components/FormStyle";
import {Controller} from "react-hook-form";
import {languages} from "../../../strings/LanguageDictionary";
import {AppContext} from "../../../App";

const LISTBOX_PADDING = 8;

function renderRow(props) {
    const {data, index, style} = props;
    return React.cloneElement(data[index], {
        style: {
            ...style,
            top: style.top + LISTBOX_PADDING,
        },
    });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
    const {children, ...other} = props;
    const itemData = React.Children.toArray(children);
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {noSsr: true});
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (child) => {
        if (React.isValidElement(child) && child.type === ListSubheader) {
            return 48;
        }

        return itemSize;
    };

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

ListboxComponent.propTypes = {
    children: PropTypes.node,
};

const useStyles = makeStyles({
    listbox: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
    },
    paper: {
        background: "var(--atl-surface-solid) !important",
        backgroundColor: "var(--atl-surface-solid) !important",
        backgroundImage: "none !important",
        color: "var(--atl-text) !important",
        border: "1px solid var(--atl-border)",
        borderRadius: "14px !important",
        boxShadow: "var(--atl-shadow) !important",
        backdropFilter: "none !important",
        WebkitBackdropFilter: "none !important",
        marginTop: 4,
    },
});

const renderGroup = (params) => [
    <ListSubheader key={params.key} component="div">
        {params.group}
    </ListSubheader>,
    params.children,
];

const VirtualizedAutocomplete = ({
                                     options,
                                     onChange: ignored,
                                     control,
                                     label,
                                     name,
                                     multiple,
                                     rules,
                                     error,
                                     disabled = false,
                                     variant = "outlined",
                                     getOptionSelected
                                 }) => {
    const classes = useStyles();
    const appContext = useContext(AppContext);
    const noneValue = "";
    return (
        <Controller
            render={({onChange, ...props}) => (
                <Autocomplete
                    id="virtualize-demo"
                    disableListWrap
                    disabled={disabled}
                    classes={classes}
                    renderGroup={renderGroup}
                    options={[noneValue, ...options]} // Aggiungo "non specificato" all'elenco
                    isOptionEqualToValue={getOptionSelected}
                    slots={{
                        listbox: ListboxComponent,
                        paper: Paper,
                    }}
                    slotProps={{
                        paper: {
                            className: classes.paper,
                        },
                    }}
                    groupBy={(option) => option && option[0] ? option[0].toUpperCase() : ""}
                    renderInput={(params) => <TextField {...params} error={!isEmpty(error)}
                                                        margin='dense'
                                                        helperText={error?.type === "validate" ? languages[appContext.state.lang].OCEAN__GLOBAL_NOT_VALID_FILES : error?.message ? languages[appContext.state.lang].OCEAN__GLOBAL_REQUIRED_FILES_ : ""}
                                                        slotProps={{
                                                            ...params.slotProps,
                                                            formHelperText: {
                                                                ...params.slotProps?.formHelperText,
                                                                style: getHelperStyle().helper
                                                            }
                                                        }} variant={variant} label={label}/>}
                    renderOption={(props, option) => (
                        <li {...props}>
                            <Typography noWrap>
                                {option === noneValue ? (
                                    <em>{languages[appContext.state.lang].OCEAN__GLOBAL_NON_SPECIFICATO}</em>
                                ) : (
                                    option
                                )}
                            </Typography>
                        </li>
                    )}
                    onChange={(e, data) => onChange(data)}
                    {...props}
                />)}
            onChange={([, data]) => data}
            defaultValue={multiple ? [] : null}
            name={name}
            control={control}
            rules={rules}
        />
    );
}

export default React.memo(VirtualizedAutocomplete)
